import { api } from "../api.ts";
import { speech, type SpeakMode } from "../speech.ts";
import { mountMemory } from "./memory.ts";
import { icon } from "../icons.ts";
import { $, $$, arrowKeys, busy, errorText, esc, load, skeleton, toast } from "../ui.ts";
import { go, signedOut, theme, type ViewContext } from "../main.ts";
import { startTour } from "../tour.ts";
import { disablePush, enablePush, pushState, type PushState } from "../push.ts";

const section = (ico: string, title: string, text: string, body: string, cls = "") =>
  `<section class="section ${cls}"><header>${icon(ico)}<div><h2>${title}</h2>${text ? `<p>${text}</p>` : ""}</div></header>${body}</section>`;
const radios = (group: string, options: [string, string, string?][], current: string, label: string) =>
  `<div class="segmented" role="radiogroup" aria-label="${label}" data-group="${group}">${options.map(([v, t, ico]) =>
    `<button type="button" role="radio" aria-checked="${v === current}" tabindex="${v === current ? 0 : -1}" data-value="${v}">${ico ? icon(ico, "sm") : ""}${t}</button>`).join("")}</div>`;

function wireRadios(group: HTMLElement, onChange: (v: string) => void) {
  const select = (b: HTMLElement) => {
    $$(group, '[role="radio"]').forEach((x) => { x.setAttribute("aria-checked", String(x === b)); x.tabIndex = x === b ? 0 : -1; });
    onChange(b.dataset.value!);
  };
  $$(group, '[role="radio"]').forEach((b) => b.addEventListener("click", () => select(b)));
  arrowKeys(group, select);
}

export function mount(main: HTMLElement, ctx: ViewContext) {
  const blocked = ctx.me.user.panic;
  queueMicrotask(() => {
    const group = main.querySelector<HTMLElement>('[data-group="speak"]');
    if (group) wireRadios(group, (v) => { speech.setMode(v as SpeakMode); toast(v === "never" ? "Vera solo responderá por escrito." : v === "voice" ? "Si le mandas una nota de voz, te contestará en voz alta." : "Leerá en voz alta todas sus respuestas."); });
  });
  main.innerHTML = `<div class="content settings">
    ${section("sun", "Apariencia", "", radios("theme", [["system", "Sistema", "monitor"], ["light", "Claro", "sun"], ["dark", "Oscuro", "moon"]], theme.preference(), "Tema"))}
    ${section("message-square", "Tutorial", "Un recorrido de un minuto por lo importante de Vera. Puedes repetirlo cuando quieras.",
      `<button class="btn" type="button" data-tour>${icon("chevron-right", "sm")} Ver el tutorial</button>`)}
    ${speech.supported() ? section("volume-2", "Respuestas en voz alta", "Vera lee sus respuestas con la voz de este dispositivo. Cada respuesta tiene además un botón para escucharla.",
      radios("speak", [["never", "Nunca"], ["voice", "Si le hablo"], ["always", "Siempre"]], speech.mode(), "Respuestas en voz alta")) : ""}
    ${section("inbox", "Avisos en este dispositivo", "Te avisa al momento cuando Vera necesita tu confirmación, aunque tengas la app cerrada.", `<div data-push>${skeleton(1)}</div>`)}
    <div data-prefs>${skeleton(3)}</div>
    ${section("history", "Memoria", "Lo que recuerda de ti y cómo lo va aprendiendo. Puedes confirmar, descartar u olvidar cualquier cosa.", "<div data-memories></div>")}
    ${section("refresh-cw", "Datos de la demostración", "Vuelve a dejar el buzón, la agenda y los documentos ficticios como al principio. El registro de auditoría se conserva.",
      `<button class="btn" type="button" data-reset>${icon("refresh-cw", "sm")} Restablecer datos de prueba</button>`)}
    ${section("octagon-alert", "Botón de pánico", "Bloquea a Vera al instante: cancela lo que esté haciendo, caduca lo pendiente y le deja solo leer. Lo levanta administración.",
      `<div data-panic>${blocked ? `<p class="chip blocked">${icon("octagon-alert")} Vera está bloqueada para ti</p>` : `<button class="btn danger" type="button" data-block>${icon("octagon-alert", "sm")} Bloquear a Vera</button>`}</div>`, "danger-zone")}
    ${section("log-out", "Sesión", `${esc(ctx.me.user.email)} · demostración`, `<button class="btn" type="button" data-logout>${icon("log-out", "sm")} Cerrar sesión</button>`)}
  </div>`;
  wireRadios($(main, '[data-group="theme"]'), (v) => theme.apply(v as "system" | "light" | "dark"));

  const prefs = $(main, "[data-prefs]");
  void load(prefs, {
    fetch: () => api.preferences(),
    render: (p) => {
      prefs.innerHTML = section("message-square", "Cómo te responde", "Siempre en texto. También puedes pedírselo en el chat.",
        `<div style="display:grid;gap:14px">${radios("tone", [["concise", "Conciso"], ["detailed", "Con detalle"]], p.tone, "Tono de las respuestas")}</div>`);
      prefs.insertAdjacentHTML("beforeend", section("message-square", "Secretaria de WhatsApp", "Vera lee tus mensajes nuevos y, si alguien te propone comer, una llamada o una reunión, mira tu agenda, lo deja como provisional y te prepara la respuesta. Nunca contesta sin tu confirmación. En la demostración, el WhatsApp es ficticio.",
        `<div style="display:grid;gap:14px">${radios("secretary", [["on", "Activada"], ["off", "Desactivada"]], p.secretary ? "on" : "off", "Secretaria de WhatsApp")}
        <div><p class="ink-2" style="margin:0 0 8px;font-size:14px">Simular que te llega un WhatsApp:</p><div class="form-actions" style="justify-content:flex-start;flex-wrap:wrap;margin:0">
          <button class="btn" type="button" data-simulate="comida">Javier te invita a comer</button>
          <button class="btn" type="button" data-simulate="conflicto">Marta propone un café que choca</button>
          <button class="btn" type="button" data-simulate="felicitacion">Rui te felicita</button></div></div></div>`));
      wireRadios($(prefs, '[data-group="secretary"]'), async (v) => {
        try { await api.updatePreferences({ secretary: v === "on" }); toast(v === "on" ? "Vera revisará tus mensajes nuevos." : "Vera no leerá tus mensajes."); }
        catch (err) { toast(errorText(err), "error"); }
      });
      $$<HTMLButtonElement>(prefs, "[data-simulate]").forEach((b) => b.addEventListener("click", async () => {
        const done = busy(b, "Enviando…");
        try { await api.simulateChat(b.dataset.simulate as "comida"); toast("Mensaje recibido. Míralo en el chat en unos segundos."); }
        catch (err) { toast(errorText(err), "error"); }
        finally { done(); }
      }));
      wireRadios($(prefs, '[data-group="tone"]'), async (v) => {
        try { await api.updatePreferences({ tone: v as "concise" | "detailed" }); toast(v === "concise" ? "Responderá de forma concisa." : "Responderá con más detalle."); }
        catch (err) { toast(errorText(err), "error"); }
      });
    },
  });

  const renderMemories = mountMemory($(main, "[data-memories]"), "Vera");

  const pushBox = $(main, "[data-push]");
  const PUSH_TEXT: Record<PushState, string> = {
    unsupported: "Este navegador no admite avisos.",
    "ios-not-installed": "En iPhone y iPad, primero instala Vera: botón Compartir → «Añadir a la pantalla de inicio», y ábrela desde ahí.",
    denied: "Los avisos están bloqueados para esta web. Permítelos en los ajustes del navegador y vuelve aquí.",
    off: "Desactivados en este dispositivo.",
    on: "Activados en este dispositivo.",
  };
  const renderPush = (state: PushState) => {
    pushBox.innerHTML = `<div style="display:grid;gap:12px"><p class="ink-2" role="status">${esc(PUSH_TEXT[state])}</p><div class="form-actions" style="justify-content:flex-start;margin-top:0">
      ${state === "off" ? `<button class="btn primary" type="button" data-push-on>${icon("inbox", "sm")} Activar avisos</button>` : ""}
      ${state === "on" ? `<button class="btn" type="button" data-push-test>${icon("send", "sm")} Enviar aviso de prueba</button><button class="btn ghost" type="button" data-push-off>Desactivar</button>` : ""}</div></div>`;
    pushBox.querySelector("[data-push-on]")?.addEventListener("click", async (e) => {
      const done = busy(e.currentTarget as HTMLButtonElement, "Activando…");
      try { const st = await enablePush(); renderPush(st); if (st === "on") toast("Avisos activados."); }
      catch (err) { done(); toast(errorText(err), "error"); }
    });
    pushBox.querySelector("[data-push-off]")?.addEventListener("click", async () => {
      try { renderPush(await disablePush()); toast("Avisos desactivados."); } catch (err) { toast(errorText(err), "error"); }
    });
    pushBox.querySelector("[data-push-test]")?.addEventListener("click", async (e) => {
      const done = busy(e.currentTarget as HTMLButtonElement, "Enviando…");
      try { const r = await api.pushTest(); toast(r.delivered ? "Aviso enviado. Debería llegarte en unos segundos." : "No se ha podido entregar; desactiva y vuelve a activar los avisos.", r.delivered ? "" : "error"); }
      catch (err) { toast(errorText(err), "error"); }
      finally { done(); }
    });
  };
  void pushState().then(renderPush).catch(() => renderPush("unsupported"));

  $(main, "[data-tour]").addEventListener("click", () => startTour({ isAdmin: ctx.me.user.role === "admin" }));
  $<HTMLButtonElement>(main, "[data-reset]").addEventListener("click", async (e) => {
    const done = busy(e.currentTarget as HTMLButtonElement, "Restableciendo…");
    try { await api.resetDemo(); toast("Datos de prueba restablecidos."); void renderMemories(); }
    catch (err) { toast(errorText(err), "error"); }
    finally { done(); }
  });

  const zone = $(main, "[data-panic]");
  zone.querySelector("[data-block]")?.addEventListener("click", () => {
    zone.innerHTML = `<div class="inline-confirm" role="group" aria-label="Confirmar bloqueo"><p><strong>¿Bloquear a Vera ahora?</strong> No podrás desbloquearla tú.</p>
      <div class="form-actions"><button class="btn danger solid" type="button" data-yes>${icon("octagon-alert", "sm")} Sí, bloquear</button><button class="btn" type="button" data-no>Cancelar</button></div></div>`;
    $(zone, "[data-no]").focus();
    $(zone, "[data-no]").addEventListener("click", () => mount(main, ctx));
    $(zone, "[data-yes]").addEventListener("click", async () => {
      try {
        const r = await api.panic();
        ctx.me.user.panic = true;
        zone.innerHTML = `<p class="chip blocked">${icon("octagon-alert")} Bloqueada: ${esc(r.actions.join(", "))}</p>`;
        toast("Vera está bloqueada.");
      } catch (err) { toast(errorText(err), "error"); }
    });
  });
  $(main, "[data-logout]").addEventListener("click", async () => {
    try { await api.logout(); } catch { /* the session may already be gone */ }
    signedOut();
    go("#/login");
  });
}
